import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DocumentsService } from '../../services/documents.service';
import { Document, Folder } from '../../interfaces/document.interface';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-documents',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <div class="documents-container">
      <div class="header">
        <h1>Documents</h1>
        <div class="actions">
          <button class="btn btn-primary" (click)="showNewFolderForm = true">
            <i class="fas fa-folder-plus"></i>
            New Folder
          </button>
          <button class="btn btn-primary" (click)="showUploadForm = true">
            <i class="fas fa-file-upload"></i>
            Upload File
          </button>
        </div>
      </div>

      <div class="breadcrumb">
        <button class="breadcrumb-item" (click)="navigateToFolder()">
          <i class="fas fa-home"></i>
          Home
        </button>
        <ng-container *ngFor="let folder of breadcrumb$ | async">
          <span class="separator">/</span>
          <button class="breadcrumb-item" (click)="navigateToFolder(folder.id)">
            {{ folder.name }}
          </button>
        </ng-container>
      </div>

      <!-- Folders Section -->
      <div class="folders-section" *ngIf="(folders$ | async)?.length">
        <div class="section-title">Folders</div>
        <div class="folders-list">
          <div *ngFor="let folder of folders$ | async" class="folder-item">
            <div class="folder-content" (click)="navigateToFolder(folder.id)">
              <i class="fas fa-folder"></i>
              <span class="folder-name">{{ folder.name }}</span>
            </div>
            <div class="folder-actions">
              <button class="btn btn-icon" (click)="editFolder(folder)" title="Edit">
                <i class="fas fa-edit"></i>
              </button>
              <button class="btn btn-icon btn-danger" (click)="deleteFolder(folder)" title="Delete">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Documents Section -->
      <div class="documents-section">
        <div class="section-title">Documents</div>
        <div class="table-container">
          <table class="documents-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Size</th>
                <th>Last Modified</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let document of documents$ | async">
                <td class="document-name">
                  <i class="fas" [class.fa-file-pdf]="document.fileType.includes('pdf')"
                                [class.fa-file-word]="document.fileType.includes('word')"
                                [class.fa-file-excel]="document.fileType.includes('excel')"
                                [class.fa-file-image]="document.fileType.includes('image')"
                                [class.fa-file]="!document.fileType.includes('pdf') && 
                                               !document.fileType.includes('word') && 
                                               !document.fileType.includes('excel') && 
                                               !document.fileType.includes('image')"></i>
                  {{ document.name }}
                </td>
                <td>{{ document.fileType }}</td>
                <td>{{ formatFileSize(document.fileSize) }}</td>
                <td>{{ document.updatedAt | date:'medium' }}</td>
                <td>
                  <div class="document-actions">
                    <a [href]="document.fileUrl" target="_blank" class="btn btn-icon" title="Download">
                      <i class="fas fa-download"></i>
                    </a>
                    <button class="btn btn-icon" (click)="editDocument(document)" title="Edit">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button class="btn btn-icon btn-danger" (click)="deleteDocument(document)" title="Delete">
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
              <tr *ngIf="!(documents$ | async)?.length">
                <td colspan="5" class="empty-message">No documents found</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- New Folder Modal -->
      <div class="modal" *ngIf="showNewFolderForm">
        <div class="modal-content">
          <h3>Create New Folder</h3>
          <form [formGroup]="folderForm" (ngSubmit)="createFolder()">
            <div class="form-group">
              <label>Folder Name</label>
              <input type="text" formControlName="name" class="form-input" 
                     placeholder="Enter folder name">
            </div>
            <div class="form-group">
              <label>Description (Optional)</label>
              <textarea formControlName="description" class="form-input" rows="3"
                        placeholder="Enter folder description"></textarea>
            </div>
            <div class="modal-actions">
              <button type="button" class="btn btn-secondary" (click)="showNewFolderForm = false">
                Cancel
              </button>
              <button type="submit" class="btn btn-primary" [disabled]="!folderForm.valid">
                Create Folder
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Upload File Modal -->
      <div class="modal" *ngIf="showUploadForm">
        <div class="modal-content">
          <h3>Upload File</h3>
          <form [formGroup]="uploadForm" (ngSubmit)="uploadFile()">
            <div class="form-group">
              <label>File Name</label>
              <input type="text" formControlName="name" class="form-input"
                     placeholder="Enter file name">
            </div>
            <div class="form-group">
              <label>Description (Optional)</label>
              <textarea formControlName="description" class="form-input" rows="3"
                        placeholder="Enter file description"></textarea>
            </div>
            <div class="form-group">
              <label>File</label>
              <input type="file" (change)="onFileSelected($event)" class="form-input">
            </div>
            <div class="modal-actions">
              <button type="button" class="btn btn-secondary" (click)="showUploadForm = false">
                Cancel
              </button>
              <button type="submit" class="btn btn-primary" [disabled]="!uploadForm.valid || !selectedFile">
                Upload File
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./documents.component.css']
})
export class DocumentsComponent implements OnInit {
  folders$: Observable<Folder[]>;
  documents$: Observable<Document[]>;
  breadcrumb$: Observable<Folder[]>;
  currentFolderId: string | undefined;
  
  folderForm: FormGroup;
  uploadForm: FormGroup;
  showNewFolderForm = false;
  showUploadForm = false;
  selectedFile: File | null = null;

  constructor(
    private documentsService: DocumentsService,
    private fb: FormBuilder
  ) {
    this.folderForm = this.fb.group({
      name: ['', Validators.required],
      description: ['']
    });

    this.uploadForm = this.fb.group({
      name: ['', Validators.required],
      description: ['']
    });

    this.folders$ = this.documentsService.getFolders();
    this.documents$ = this.documentsService.getDocuments();
    this.breadcrumb$ = this.documentsService.getBreadcrumb();
  }

  ngOnInit() {
    this.loadContent();
  }

  loadContent(folderId?: string) {
    this.currentFolderId = folderId;
    this.folders$ = this.documentsService.getFolders(folderId);
    this.documents$ = this.documentsService.getDocuments(folderId);
    if (folderId) {
      this.breadcrumb$ = this.documentsService.getBreadcrumb(folderId);
    } else {
      this.breadcrumb$ = new Observable(subscriber => subscriber.next([]));
    }
  }

  navigateToFolder(folderId?: string) {
    this.loadContent(folderId);
  }

  async createFolder() {
    if (this.folderForm.valid) {
      try {
        const { name, description } = this.folderForm.value;
        await this.documentsService.createFolder(name, description, this.currentFolderId);
        this.folderForm.reset();
        this.showNewFolderForm = false;
      } catch (error) {
        console.error('Error creating folder:', error);
      }
    }
  }

  async editFolder(folder: Folder) {
    const newName = prompt('Enter new folder name:', folder.name);
    if (newName && newName !== folder.name) {
      try {
        await this.documentsService.updateFolder(folder.id, { name: newName });
      } catch (error) {
        console.error('Error updating folder:', error);
      }
    }
  }

  async deleteFolder(folder: Folder) {
    if (confirm('Are you sure you want to delete this folder and all its contents?')) {
      try {
        await this.documentsService.deleteFolder(folder.id);
      } catch (error) {
        console.error('Error deleting folder:', error);
      }
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.selectedFile = input.files[0];
      if (!this.uploadForm.get('name')?.value) {
        this.uploadForm.patchValue({ name: this.selectedFile.name });
      }
    }
  }

  async uploadFile() {
    if (this.uploadForm.valid && this.selectedFile) {
      try {
        const { name, description } = this.uploadForm.value;
        await this.documentsService.uploadDocument(
          this.selectedFile,
          name,
          description,
          this.currentFolderId
        );
        this.uploadForm.reset();
        this.selectedFile = null;
        this.showUploadForm = false;
      } catch (error) {
        console.error('Error uploading file:', error);
      }
    }
  }

  async editDocument(document: Document) {
    const newName = prompt('Enter new file name:', document.name);
    if (newName && newName !== document.name) {
      try {
        await this.documentsService.updateDocument(document.id, { name: newName });
      } catch (error) {
        console.error('Error updating document:', error);
      }
    }
  }

  async deleteDocument(document: Document) {
    if (confirm('Are you sure you want to delete this file?')) {
      try {
        await this.documentsService.deleteDocument(document.id);
      } catch (error) {
        console.error('Error deleting document:', error);
      }
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
} 